import {
  Component,
  Input,
  ElementRef,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ButtonComponent } from '../../../molecules/button/button.component';
import { ButtonType } from '../../../../shared/enums/button-type.enums';
import { IconComponent } from '../../../atoms/icon/icon.component';
import { StaticGraphicComponent } from '../../../molecules/static-graphic/static-graphic.component';
import { MultimediaListComponent } from '../../multimedia-list/multimedia-list.component';
import { UtilitiesCardsComponent } from '../../../molecules/utilities-cards/utilities-cards.component';
import { ModalFunctionType } from '../../../../shared/enums/modal-function-type.enums';
import { TextBlockComponent } from '../../../atoms/text-block/text-block.component';
import { AccordionItem } from '../../../../shared/interfaces/accordion-item.interface';
import { MultimediaItem } from '../../../../shared/interfaces/multimedia-item.interface';
import { ModalFunctionItem } from '../../../../shared/interfaces/modal-function-item.interface';
import { MultimediaList } from '../../../../shared/interfaces/multimedia-list.interface';
import {
  UtilitiesCards,
  UtilitiesCardsItem,
} from '../../../../shared/interfaces/utilities-cards.interface';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeService } from '../../../../services/fontawesome.service';
import { CaasComponent } from '../../../../shared/interfaces/caas/caas.interface';
import { CaasRoutesService } from '../../../../services/caas-routes.service';
import { TextBlock } from '../../../../shared/interfaces/text-block.interface';
import { ImagesService } from '../../../../services/images.service';
import { Router, ActivatedRoute } from '@angular/router';
import { CapitalizePipe } from '../../../capitalize.pipe';
import { BaseAccordionComponent } from '../base-accordion.component';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { safeAssign } from '../../../../shared/utils/safe-assign.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-accordion',
  standalone: true,
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    MultimediaListComponent,
    UtilitiesCardsComponent,
    TextBlockComponent,
    StaticGraphicComponent,
    CapitalizePipe,
    NgxSkeletonLoaderModule,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class AccordionComponent
  extends BaseAccordionComponent
  implements OnInit, OnChanges
{
  @Input() accordionItems: AccordionItem[] = [];
  @Input() MultimediaItems: MultimediaItem[] = [];
  @Input() multimediaLists: MultimediaList[] = [];
  @Input() utilitiesCardsList: UtilitiesCards[] = [];
  @Input() textBlocks?: TextBlock[];
  @Input() graphs?: CaasComponent[];
  @Input() modalFunctionItems: ModalFunctionItem[] = [];
  @Input() hasIcon: boolean = false;
  @Input() caasData: CaasComponent = {} as CaasComponent;

  accordion: ButtonType = ButtonType.OPEN_CLOSE_SECONDARY;
  AccordionToggle: ElementRef = {} as ElementRef;
  ModalFunctionType = ModalFunctionType;

  public isLoading = true;

  constructor(
    private readonly fontAwesomeService: FontAwesomeService,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly imagesService: ImagesService,
    protected override cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) protected override document: Document,
    protected override router: Router,
    protected override route: ActivatedRoute,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    super(cdr, route, document, router);
  }

  protected override getItems() {
    return this.accordionItems;
  }

  protected override hasCaasData(): boolean {
    return !!this.caasData;
  }

  protected override processCaasData(): void {
    this.setCaasContent();
    this.setCaasOpenState(
      this.accordionItems,
      this.caasData?.formData?.st_expand?.value?.identifier
    );
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  // Special getter for accordion item ID
  protected override getItemId(item: AccordionItem): string {
    return item.key;
  }

  getIcon(name: string): string {
    const isIconAvailable = this.fontAwesomeService.checkIconAvailable(
      'fal' as IconPrefix,
      name as IconName
    );
    if (isIconAvailable) {
      return `${name}`;
    } else {
      return `user`;
    }
  }

  getRightIcon(rightIcon: string) {
    if (rightIcon.length) {
      switch (rightIcon) {
        case 'tooltip':
          return 'info-circle';
        case 'modal':
          return 'list';
        default:
          return rightIcon;
      }
    } else {
      return '';
    }
  }

  setAccordionItemsAndGraphs(): void {
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_items?.value?.length) {
          this.accordionItems = [];

          this.caasData.formData.st_items.value.forEach((item, index) => {
            this.processAccordionItem(item, index);
          });
        }
      },
      'Top-level setAccordionItemsAndGraphs block',
      this.errorHandler
    );
  }

  private processAccordionItem(item: CaasComponent, index: number): void {
    safeAssign(
      () => {
        const key = item.identifier;
        const content: CaasComponent[] = this.buildContentArray(
          item,
          key,
          index
        );

        // Safely extract individual fields
        let label = '';
        let name = '';
        let icon = 'user';
        let Headline = '';

        safeAssign(
          () => {
            label = item.formData?.st_header?.value ?? '';
          },
          `Assigning label for accordion item[${index}]`,
          this.errorHandler
        );

        safeAssign(
          () => {
            name = item.formData?.st_header?.value ?? '';
          },
          `Assigning name for accordion item[${index}]`,
          this.errorHandler
        );

        safeAssign(
          () => {
            icon =
              typeof item.formData?.st_icon?.value === 'string'
                ? item.formData.st_icon.value
                : 'user';
          },
          `Assigning icon for accordion item[${index}]`,
          this.errorHandler
        );

        safeAssign(
          () => {
            Headline = item.formData?.st_header?.value ?? '';
          },
          `Assigning Headline for accordion item[${index}]`,
          this.errorHandler
        );

        // Push the accordion item safely
        safeAssign(
          () => {
            this.accordionItems.push({
              key,
              label,
              name,
              icon,
              Headline,
              content,
            });
          },
          `Pushing accordionItems[${index}]`,
          this.errorHandler
        );
      },
      `Processing accordion item[${index}]`,
      this.errorHandler
    );
  }

  private buildContentArray(
    item: CaasComponent,
    key: string,
    index: number
  ): CaasComponent[] {
    const content: CaasComponent[] = [];
    safeAssign(
      () => {
        const childItems = item.formData?.st_items?.value;
        if (childItems?.length) {
          childItems.forEach((childItem, childIndex) => {
            safeAssign(
              () => {
                content.push({
                  ...childItem,
                  identifier: key,
                });
              },
              `Pushing childItem[${childIndex}] for accordion item[${index}]`,
              this.errorHandler
            );
          });
        }
      },
      `Building content array for accordion item[${index}]`,
      this.errorHandler
    );
    return content;
  }

  private mapLink(link: CaasComponent): UtilitiesCardsItem {
    const mappedLink: UtilitiesCardsItem = {} as UtilitiesCardsItem;

    safeAssign(
      () => {
        mappedLink.label = link?.formData?.st_header?.value ?? '';
      },
      'Assigning link.label',
      this.errorHandler
    );

    safeAssign(
      () => {
        mappedLink.rightIcon = this.getRightIcon(
          link?.formData?.st_infotype?.value?.identifier ?? ''
        );
      },
      'Assigning link.rightIcon',
      this.errorHandler
    );

    safeAssign(
      () => {
        mappedLink.badgeLabel = link?.formData?.st_badge?.value ?? undefined;
      },
      'Assigning link.badgeLabel',
      this.errorHandler
    );

    safeAssign(
      () => {
        const tooltipVal = link?.formData?.st_tooltip?.value ?? '';
        mappedLink.tooltip = tooltipVal.length
          ? {
              id: link.identifier,
              description: `<p>${tooltipVal}</p>`,
            }
          : undefined;
      },
      'Assigning link.tooltip',
      this.errorHandler
    );

    safeAssign(
      () => {
        let linkType: string | undefined = undefined;
        let linkUrl: string | undefined = undefined;
        safeAssign(
          () => {
            linkType =
              link?.formData?.st_link?.value?.formData?.lt_link_type?.value
                ?.identifier;
          },
          'Assigning linkType',
          this.errorHandler
        );

        safeAssign(
          () => {
            linkUrl =
              linkType === 'external'
                ? link?.formData?.st_link?.value?.formData?.lt_link_external
                    ?.value
                : (this.caasRoutesService.getSeoRouteByIdentifier(
                    link?.formData?.st_link?.value?.formData?.lt_link_internal
                      ?.value?.identifier ?? '',
                    this.router.config
                  )?.path ?? '404');
          },
          'Assigning linkUrl',
          this.errorHandler
        );

        mappedLink.target = linkType === 'external' ? '_blank' : '_self';

        mappedLink.url = linkUrl;
      },
      'Assigning link.target/url',
      this.errorHandler
    );

    safeAssign(
      () => {
        mappedLink.title =
          link?.formData?.st_link?.value?.formData?.lt_link_title?.value ?? '';
      },
      'Assigning link.title',
      this.errorHandler
    );

    safeAssign(
      () => {
        let headline = '';
        let description = '';
        safeAssign(
          () => {
            headline = link?.formData?.st_modal_headline?.value ?? '';
          },
          'Assigning headline',
          this.errorHandler
        );
        safeAssign(
          () => {
            description = link?.formData?.st_modal_text?.value ?? '';
          },
          'Assigning description',
          this.errorHandler
        );
        mappedLink.modal =
          headline || description?.length
            ? {
                title: headline,
                description: `<p>${description}</p>`,
              }
            : undefined;
      },
      'Assigning link.modal',
      this.errorHandler
    );

    return mappedLink;
  }

  private mapItem(card: CaasComponent, item: CaasComponent): UtilitiesCards {
    const mappedItem: UtilitiesCards = {} as UtilitiesCards;

    safeAssign(
      () => {
        mappedItem.title = item?.formData?.st_header?.value ?? undefined;
      },
      'Assigning item.title',
      this.errorHandler
    );

    safeAssign(
      () => {
        mappedItem.key = card.identifier;
      },
      'Assigning item.key',
      this.errorHandler
    );

    safeAssign(
      () => {
        mappedItem.items =
          item?.formData?.st_items?.value?.map((link) => this.mapLink(link)) ??
          [];
      },
      'Assigning item.items',
      this.errorHandler
    );

    return mappedItem;
  }

  setutilitiesCardsList(): void {
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_items?.value?.length) {
          this.utilitiesCardsList = this.caasData.formData.st_items.value
            .flatMap((card) => {
              const cardItems = card?.formData?.st_items?.value;
              if (!cardItems) return [];
              return cardItems.map((item) => this.mapItem(card, item));
            })
            .filter((item) => item.items?.length);
        }
      },
      'Top-level utilitiesCardsList block',
      this.errorHandler
    );
  }

  setCaasContent() {
    safeAssign(
      () => {
        if (this.caasData?.formData?.st_header) {
          this.title = this.caasData.formData.st_header?.value;
        }
      },
      'Assigning title from st_header',
      this.errorHandler
    );

    safeAssign(
      () => {
        if (this.caasData?.formData?.st_show_icons?.value) {
          this.hasIcon = this.caasData.formData.st_show_icons.value;
        }
      },
      'Assigning hasIcon from st_show_icons',
      this.errorHandler
    );
    this.setutilitiesCardsList();
    this.setAccordionItemsAndGraphs();
  }
}
